import { supabase } from './supabase';
import { compressImage } from './media';
import type { PlacePhoto } from '../types';

/** Hosts can attach up to this many photos to one event/place. */
export const MAX_PLACE_PHOTOS = 3;

interface PhotoSource {
  photos?: PlacePhoto[] | null;
  photo_url?: string | null;
  photo_focal?: { x: number; y: number } | null;
}

/**
 * The photos to show for a place/event, cover first. Falls back to the legacy
 * single photo_url/photo_focal for data saved before multi-photo support.
 */
export function getPlacePhotos(src: PhotoSource): PlacePhoto[] {
  const list = (src.photos ?? []).filter((p) => p?.url);
  if (list.length > 0) return list.slice(0, MAX_PLACE_PHOTOS);
  return src.photo_url ? [{ url: src.photo_url, focal: src.photo_focal ?? null }] : [];
}

/** Field values to persist for a photo list: the array plus the cover mirror. */
export function photoFields(photos: PlacePhoto[]): {
  photos: PlacePhoto[];
  photo_url: string | null;
  photo_focal: { x: number; y: number } | null;
} {
  const list = photos.filter((p) => p.url).slice(0, MAX_PLACE_PHOTOS);
  return { photos: list, photo_url: list[0]?.url ?? null, photo_focal: list[0]?.focal ?? null };
}

/**
 * Compress (max 960px JPEG — see lib/media) and upload one place photo to the
 * host's folder in the place-photos bucket. Returns the public URL, or null if
 * the upload failed. Compressing here is what keeps phone-camera originals
 * (often 5–12MB) from being stored and served as-is to travelers.
 */
export async function uploadPlacePhoto(hostId: string, file: File): Promise<string | null> {
  const optimized = await compressImage(file);
  const path = `${hostId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${optimized.name}`;
  const { data, error } = await supabase.storage.from('place-photos').upload(path, optimized, { upsert: true });
  if (error || !data) return null;
  return supabase.storage.from('place-photos').getPublicUrl(data.path).data.publicUrl;
}
